import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DISMISS_DRAG_PX = 120;

// Reusable bottom sheet rendered via a React portal into document.body so that
// ancestor overflow/transform/perspective CSS never clips or mispositions it.
// The drag handle supports a real swipe-down-to-dismiss gesture (01-driver-app-
// shell.md's "swipe-up dashboard" — closing is the swipe gesture; opening stays
// a tap/controlled `open` prop, which is simpler and just as discoverable).
function BottomSheet({ open, onClose, title, children, footer }) {
  const [dragY, setDragY] = useState(0);
  const draggingRef = useRef(false);
  const startYRef = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) setDragY(0);
  }, [open]);

  const handlePointerDown = (e) => {
    draggingRef.current = true;
    startYRef.current = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const delta = e.clientY - startYRef.current;
    setDragY(Math.max(0, delta));
  };
  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (dragY > DISMISS_DRAG_PX) {
      onClose?.();
    }
    setDragY(0);
  };

  const sheet = (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1500, pointerEvents: open ? 'auto' : 'none' }}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(15,23,42,0.5)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0,
          transition: 'opacity 280ms ease-out',
        }}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column',
          maxHeight: '88vh',
          borderRadius: '24px 24px 0 0',
          background: '#F8FAFC',
          boxShadow: '0 -8px 40px rgba(15,23,42,0.18)',
          transform: open ? `translateY(${dragY}px)` : 'translateY(100%)',
          transition: draggingRef.current ? 'none' : 'transform 300ms cubic-bezier(0.32,0.72,0,1)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          touchAction: 'none',
        }}
      >
        {/* Drag handle — swipe down to dismiss */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab' }}
        >
          <div style={{ width: 36, height: 5, borderRadius: 3, background: '#CBD5E1' }} />
        </div>

        {/* Header */}
        {title && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 20px 14px',
            borderBottom: '1px solid #E2E8F0',
          }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', letterSpacing: -0.3 }}>
              {title}
            </span>
            <button
              onClick={onClose}
              style={{
                width: 32, height: 32, borderRadius: 8, border: 'none',
                background: '#F1F5F9', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, lineHeight: 1, color: '#64748B',
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Scrollable content */}
        <div style={{ minHeight: 0, flex: 1, overflowY: 'auto', padding: '16px 16px 20px' }}>
          {children}
        </div>

        {footer && (
          <div style={{ borderTop: '1px solid #E2E8F0', background: '#fff', padding: '12px 16px' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

export default BottomSheet;
